/**
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║                    🌸 READ ME FIRST 🌸                         ║
 * ║              Delivery Date & Cutoff Time Logic                  ║
 * ║         Last reviewed: April 5, 2026 by Claude (Opus)          ║
 * ╚══════════════════════════════════════════════════════════════════╝
 *
 * THIS FILE IS THE "LEGACY" SYSTEM (System A).
 * There is also a "MAIN" system in snippets/same-day-order.liquid (System B).
 * Both systems run simultaneously and write to the same DOM elements.
 * System B runs once on DOM ready; System A runs every 1 second and overwrites.
 * As of April 2026, both produce identical results — but if you change one,
 * you MUST check the other.
 *
 * ── HOW THE TWO SYSTEMS INTERACT ──────────────────────────────────
 *
 *   header.liquid          →  Sets window.blockOutDates, window.todaysCutoffTime
 *   base.js (THIS FILE)    →  Reads those globals, updates DOM every 1s
 *   same-day-order.liquid  →  Has its OWN MetaObject reads + datepicker + countdown
 *
 *   DOM elements both write to:
 *     #next-workday-name   ("today" / "tomorrow" / weekday name)
 *     #next-workday-date   ("MM/DD/YYYY")
 *
 * ── CONFUSING VARIABLE NAMING (READ THIS!) ────────────────────────
 *
 *   window.blockOutDates (set in header.liquid):
 *     MISLEADING NAME! This is populated from the "weekend_dates" MetaObject,
 *     which contains dates when weekend delivery IS ENABLED.
 *     But base.js treats them as BLOCKED dates (line ~55).
 *     Net effect: weekends are always blocked by the isWeekend check (line ~56),
 *     so this array being "wrong" doesn't matter functionally.
 *     DO NOT "fix" this naming without updating both systems.
 *
 *   window.todaysCutoffTime (set in header.liquid):
 *     String like "1:00 PM" from the cutoff_times MetaObject.
 *     Used by getCutoffTime() below.
 *
 * ── KEY FUNCTIONS ─────────────────────────────────────────────────
 *
 *   getCutoffTime(dayjs)        → Returns dayjs object for today's cutoff
 *   nextDeliveryDate(bool, dayjs) → Finds next non-blocked, non-weekend date
 *   drawTimeUpdates()           → Main loop (runs every 1s via setInterval)
 *                                  Updates countdown, "today/tomorrow" text,
 *                                  and refreshes datepicker minDate
 *
 * ── METAOBJECTS INVOLVED (3 types across 2 systems) ──────────────
 *
 *   cutoff_times / main-cutoff-time:
 *     - cuttoff_time: "1:00 PM" (yes, "cuttoff" is a typo — don't rename,
 *       it's the MetaObject field key and referenced everywhere)
 *     - date_time: optional admin override for a specific day's cutoff
 *
 *   blockout_dates (used by same-day-order.liquid, NOT base.js):
 *     - main-manual-date-list: Dates the store is CLOSED (weekends, holidays)
 *     - sundays: (optional) Additional Sunday blocks
 *     - vday-sold-out: (optional) Valentine's sold-out dates
 *     - vday_exceptions_2026: Dates to UN-block for V-Day specials
 *
 *   weekend_dates / enable-weekend-dates:
 *     - dates_list: Dates when weekend delivery IS ALLOWED
 *     - Used by header.liquid (confusingly as "blockOutDates") and by
 *       same-day-order.liquid (correctly as enabled weekends)
 *
 * ── STORE TIMEZONE ────────────────────────────────────────────────
 *   Texas-based stores use America/Chicago (Central Time), California is Pacific Time
 *   shopID conditionals in header.liquid set this.
 *   SA: 92634710300 | Temple & Belton: 70513426600 | SF: 88728830268
 * ══════════════════════════════════════════════════════════════════
 */

function romanNumeral(num) {
  if (!+ num)
    return false;

  var digits = String(+ num).split(""),
    key = ["", "C", "CC", "CCC", "CD", "D", "DC", "DCC", "DCCC", "CM", "", "X", "XX", "XXX", "XL", "L", "LX", "LXX", "LXXX", "XC", "", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX"],
    roman = "",
    i = 3;
  while (i--)
    roman = (key[+ digits.pop() + (i * 10)] || "") + roman;

  return Array(+ digits.join("") + 1).join("M") + roman;
}

// MODERNIZED: Use DayJS for consistent timezone handling (Matches Liquid logic)
function getCurrentTime() {
    if(window.dayjs && window.dayjs_plugin_timezone) {
        // Return DayJS object for America/Chicago
        return dayjs().tz('America/Chicago');
    } else {
        // Fallback for safety (though DayJS should be loaded)
        return new Date();
    }
}

// MODERNIZED: Use DayJS logic
function getCutoffTime(currentDayjs) {
    // Assuming 'todaysCutoffTime' is global, e.g. "1:00 PM"
    if(!window.todaysCutoffTime) return currentDayjs.endOf('day');

    const [time, modifier] = todaysCutoffTime.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') hours = '00';
    if (modifier === 'PM' && hours !== '12') hours = parseInt(hours, 10) + 12;

    return currentDayjs.clone().hour(parseInt(hours)).minute(parseInt(minutes)).second(0);
}

// MODERNIZED: Use DayJS logic
function nextDeliveryDate(beforeCutoff, currentDayjs) {
  let deliveryDate = currentDayjs.clone();

  if (!beforeCutoff) {
     // If after cutoff, start checking from tomorrow
     deliveryDate = deliveryDate.add(1, 'day');
  }

  let deliveryDateFound = false;

  while (!deliveryDateFound) { 
    const potentialDateString = deliveryDate.format('YYYY-MM-DD');
    const dayOfWeek = deliveryDate.day(); // 0=Sun, 6=Sat

    // Check Global Blockout List & Weekends
    const isBlocked = (window.blockOutDates && window.blockOutDates.includes(potentialDateString));
    const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6);
    // Weekend exception: check if this specific date is in the enabledWeekendDates list
    // (e.g., Mother's Day, Valentine's weekend). Dates are managed via the
    // weekend_dates MetaObject in Shopify Admin — no code change needed to add new dates.
    const isEnabledWeekend = isWeekend && window.enabledWeekendDates &&
       window.enabledWeekendDates.includes(potentialDateString);
    if (!isBlocked && (!isWeekend || isEnabledWeekend)) {
      deliveryDateFound = true; 
      break;
    }
    deliveryDate = deliveryDate.add(1, 'day');
  }
  return deliveryDate;
}


async function drawTimeUpdates() {
  // Require DayJS for this new logic to work safely
  if(!window.dayjs) return;

  const nowSA = getCurrentTime(); // DayJS Object (Chicago)
  const cutoffTime = getCutoffTime(nowSA);
  
  let beforeCutoff = nowSA.isBefore(cutoffTime);
  
  // Double check if today is blocked entirely
  if (window.blockOutDates && window.blockOutDates.includes(nowSA.format('YYYY-MM-DD'))) {
      beforeCutoff = false;
  }

  const deliveryDate = nextDeliveryDate(beforeCutoff, nowSA);

  // If the calculated next delivery date is technically "today", re-confirm cutoff
  if (deliveryDate.isSame(nowSA, 'day')) {
     beforeCutoff = true;
  } else {
     beforeCutoff = false;
  }
  
  // Refresh Calendar Logic (Legacy support)
  if (calendar && calendarLastUpdated > 120) {
    const deliveryDateString = deliveryDate.format('YYYY-MM-DD');
    
    // Safety check for global 'datepicker' object
    if(typeof datepicker !== 'undefined') {
        datepicker.setOptions({
          minDate: deliveryDateString
        })
        datepicker.refresh();
    }
    calendarLastUpdated = 0;
  }
  calendarLastUpdated++;
  
  // Update "Next Workday" UI Elements if they exist
  const nextName = document.getElementById("next-workday-name");
  const nextDate = document.getElementById("next-workday-date");
  const tomorrow = nowSA.add(1, 'day');

  if (window.todaysCutoffTime && nextName && nextDate) {
    if (nowSA.isSame(deliveryDate, 'day') && beforeCutoff) {
      nextName.innerHTML = "today";
    } else if (tomorrow.isSame(deliveryDate, 'day')) {
      nextName.innerHTML = "tomorrow";
    } else {
      nextName.innerHTML = deliveryDate.format('dddd'); // Full weekday name
    }
    nextDate.innerHTML = deliveryDate.format('MM/DD/YYYY');
  }
}

let calendar;
let datepicker;
var calendarLastUpdated = 120;

window.addEventListener('DOMContentLoaded', () => {
  // Run updates every second
  setInterval(drawTimeUpdates, 1000);
});


function addToCart(maidProductID, original_price_in = null) {
  let id = $('input[name=upgradeRadio]:checked').val();

  if (id == null) {
    id = maidProductID;
  }

  let formData = {
    items: [
      {
        id: id,
        quantity: 1,
        properties: {}
      }
    ]
  };

  // Add discounted price as a property
  if (original_price_in !== null && !isNaN(original_price_in)) {
    formData.items[0].properties["original_price_in"] = `$${(parseFloat(original_price_in) / 100).toFixed(2)}`;
  }
  
  fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(formData)
  })
    .then(response => {
      window.location.href = "/cart";
    })
    .catch((error) => {
      console.error('Error:', error);
    });
}

function deleteFromCart(id) {
  let formData = {
    updates: {
      [id]: 0
    }
  };
  fetch(window.Shopify.routes.root + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(formData)
  })
    .then(response => {
      location.reload();
    })
    .catch((error) => {
      console.error('Error:', error);
    });
}